import { createRootRoute, Outlet, useMatches, useRouter } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { Suspense, useEffect } from 'react';

import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { LoadingScreen } from '@/components/common/LoadingScreen';
import { CadenceRedirectMount } from '@/components/shell/CadenceRedirectMount';
import { PlatformFooter } from '@/components/common/PlatformFooter';
import { MyndwellMark } from '@/components/ui/brand-mark';
import { Button } from '@/components/ui/button';

export const Route = createRootRoute({
  component: RootComponent,
  errorComponent: RouteErrorComponent,
  pendingComponent: LoadingScreen,
});

const BASE_TITLE = 'Myndwell';

interface SurfaceMatch {
  staticData?: { surface?: string };
}

// Document title follows the active surface — clinicians see "Myndwell ·
// Clinical workspace", org admins see "· Organization portal", etc. Auth
// flows and other ungated routes fall back to the bare brand. The deepest
// match with a `surface` field wins (the parent layouts each declare it),
// so nested routes don't need to repeat themselves.
function useSurfaceTitle(): void {
  const matches = useMatches() as unknown as SurfaceMatch[];
  let surface: string | undefined;
  for (const m of matches) {
    if (m.staticData?.surface !== undefined) {
      surface = m.staticData.surface;
    }
  }
  useEffect(() => {
    document.title = surface !== undefined ? `${BASE_TITLE} · ${surface}` : BASE_TITLE;
  }, [surface]);
}

function RootComponent() {
  useSurfaceTitle();
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>
        <Outlet />
      </Suspense>
      <CadenceRedirectMount />
    </ErrorBoundary>
  );
}

function RouteErrorComponent({ error }: { error: Error }) {
  const router = useRouter();
  return (
    <div className="bg-background flex min-h-screen flex-col px-6 py-12">
      <div className="flex flex-1 items-center justify-center">
        <div className="bg-surface flex max-w-md flex-col items-center gap-4 rounded-xl border border-(--color-border) p-8 text-center shadow-xs">
          <MyndwellMark size="sm" tone="ink" />
          <div className="bg-error-500/10 text-error-500 flex h-12 w-12 items-center justify-center rounded-md">
            <AlertTriangle className="h-6 w-6" strokeWidth={1.5} aria-hidden="true" />
          </div>
          <div className="space-y-1">
            <h1 className="text-body-md font-medium text-neutral-900">Something went wrong.</h1>
            <p className="text-body-md text-neutral-500">
              {import.meta.env.DEV ? error.message : 'Please try again in a moment.'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                router.history.back();
              }}
            >
              Back
            </Button>
            <Button
              onClick={() => {
                void router.invalidate();
              }}
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
      <PlatformFooter className="mx-auto w-full max-w-md" />
    </div>
  );
}
